import { ImportantContactDtos } from '@lmc/ts-common'
import { Button, Descriptions, Heading, MoreInfoLink } from '@lmc/ui-kit'
import MailIcon from '@lmc/ui-kit/src/general/Icon/customShapes/Mail'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type FamilyViewProps = {
  items?: DescriptionsItemType[]
  title: string
  icon?: ReactNode
  action?: () => void
  link?: string
  isEditMode?: boolean
  subtitle?: React.ReactNode
  className?: string
  hasAccess: boolean
  hasBeenInvited: boolean
  hasAcceptedInvitation: boolean
  onResendInvite: () => void
  onCreateInvite: () => void
  isSendingInvite: boolean
  residentName: string
  contact: Pick<ImportantContactDtos, 'email'>
}

const FamilyView = ({
  items,
  title,
  className,
  hasAccess,
  hasBeenInvited,
  hasAcceptedInvitation,
  onResendInvite,
  onCreateInvite,
  isSendingInvite,
  residentName,
  contact,
}: FamilyViewProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const filteredItems = items?.filter((item) => Boolean(item))
  const hasEmail = Boolean(contact.email)

  const showCreateInviteButton =
    !hasBeenInvited && !hasAcceptedInvitation && hasEmail
  const showResendInviteButton = hasBeenInvited && !hasAcceptedInvitation
  const showContent = hasAccess && hasBeenInvited
  const showInvitationText = !hasBeenInvited && hasEmail
  const showNoEmailText = !hasBeenInvited && !hasEmail

  return (
    <MainWrapper className={className}>
      <TopWrapper>
        <TitlesWrapper>
          <HeadingWrapper>
            <Heading level={3}>{title}</Heading>
            <MoreInfoLink helpArticleUrl="" />
          </HeadingWrapper>
          {showInvitationText && (
            <TextWithMargins>
              {t(supConstants.INVITATION_TEXT, {
                name: residentName,
              })}
            </TextWithMargins>
          )}
          {showNoEmailText && (
            <TextWithMargins>{t(supConstants.NO_EMAIL_TEXT)}</TextWithMargins>
          )}
        </TitlesWrapper>

        {showResendInviteButton && (
          <StyledButton
            isLoading={isSendingInvite}
            onClick={onResendInvite}
            variant="text"
          >
            {MailIcon}
            {t(supConstants.RESEND_INVITE)}
          </StyledButton>
        )}
        {showCreateInviteButton && (
          <StyledButton
            isLoading={isSendingInvite}
            onClick={onCreateInvite}
            variant="text"
          >
            {MailIcon}
            {t(supConstants.CREATE_INVITE)}
          </StyledButton>
        )}
      </TopWrapper>
      {showContent && (
        <>
          <DividerWithMargins />
          <Descriptions items={filteredItems} bordered column={1} />
        </>
      )}
    </MainWrapper>
  )
}

const MainWrapper = styled.div``
const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const TitlesWrapper = styled.div``
const StyledButton = styled(Button)`
  white-space: nowrap;
`
const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DividerWithMargins = styled(Divider)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const TextWithMargins = styled(Text)`
  display: block;
  margin-right: 24px;
`

export default FamilyView
